import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import MatchCard from "@/components/MatchCard";
import PremiumDialog from "@/components/PremiumDialog";
import { Lock, Sparkle, ShieldCheck, EyeSlash, CaretDown, CaretUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import usePageMeta from "@/lib/usePageMeta";
import QuietModeGuard from "@/components/QuietModeGuard";

export default function Matches() {
  usePageMeta({ title: "Matches", description: "Meet the women whose reproductive-health journey mirrors yours, ranked by shared conditions, treatments, and life stage." });
  const { user, refresh } = useAuth();
  const [data, setData] = useState({ matches: [], total: 0, premium: false, hidden: 0, verified_only: false });
  const [loading, setLoading] = useState(true);
  const [openPremium, setOpenPremium] = useState(false);
  const [savingPref, setSavingPref] = useState(false);

  const load = async () => {
    const r = await api.get("/matches", { params: { limit: 30 } });
    setData(r.data); setLoading(false);
  };
  useEffect(() => { load(); }, [user?.premium, user?.verified_only_matches]);

  const toggleVerifiedOnly = async () => {
    setSavingPref(true);
    try {
      await api.post("/settings", { verified_only_matches: !user?.verified_only_matches });
      await refresh();
      toast.success(!user?.verified_only_matches ? "Showing phone-verified peers only" : "Showing all peers");
    } catch { toast.error("Could not update preference"); }
    setSavingPref(false);
  };

  return (
    <QuietModeGuard><MatchesInner
      data={data} loading={loading} openPremium={openPremium} setOpenPremium={setOpenPremium}
      user={user} savingPref={savingPref} toggleVerifiedOnly={toggleVerifiedOnly} load={load}
    /></QuietModeGuard>
  );
}

function MatchesInner({ data, loading, openPremium, setOpenPremium, user, savingPref, toggleVerifiedOnly, load }) {
  return (
    <div className="max-w-5xl">
      <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-3"><span className="rule" />Lived experience matches</div>
      <h1 className="font-display text-4xl sm:text-5xl leading-tight">The women whose paths<br />mirror yours.</h1>
      <p className="mt-4 text-ink/70 max-w-xl">
        Ranked by our matching engine on condition, symptoms, treatments, life stage, and goals.
        {data.total > 0 && <span className="ml-1 text-muted-foreground">Showing {data.matches.length} of {data.total}.</span>}
      </p>

      {/* Verified-only toggle */}
      <div className="mt-8 flex flex-wrap items-center gap-3 bg-white border border-border rounded-2xl p-4 shadow-soft"
           data-testid="verified-only-row">
        <div className="w-9 h-9 rounded-full bg-sage-50 border border-sage-100 grid place-items-center shrink-0">
          <ShieldCheck size={18} weight="duotone" className="text-sage-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg leading-tight">Match only with phone-verified peers</div>
          <div className="text-xs text-muted-foreground">Higher-trust circle. Fewer matches, more real.</div>
        </div>
        <button onClick={toggleVerifiedOnly} disabled={savingPref}
                data-testid="verified-only-toggle"
                aria-pressed={user?.verified_only_matches ? "true" : "false"}
                className={`relative w-12 h-7 rounded-full transition-colors ${
                  user?.verified_only_matches ? "bg-coral" : "bg-border"
                } disabled:opacity-60`}>
          <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            user?.verified_only_matches ? "translate-x-5" : "translate-x-0"
          }`} />
        </button>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-5 stagger" data-testid="matches-grid">
        {loading ? <div className="text-muted-foreground">Loading…</div>
          : data.matches.length === 0 ? (
            <div className="text-muted-foreground">
              {data.verified_only
                ? "No phone-verified matches yet, turn the toggle off to see everyone."
                : "No matches yet, new women join every week."}
            </div>
          )
          : data.matches.map(m => <MatchCard key={m.user_id} match={m} onChanged={load} />)}
      </div>

      {data.hidden_count > 0 && (
        <HiddenMatchesSection onChanged={load} />
      )}

      {!data.premium && data.hidden > 0 && (
        <div className="mt-8 bg-white border border-border rounded-2xl p-8 shadow-card text-center" data-testid="matches-upgrade-block">
          <Lock size={30} weight="duotone" className="text-coral-700 mx-auto" />
          <div className="font-display text-2xl mt-3">{data.hidden} more women waiting.</div>
          <p className="mt-2 text-ink/70 text-sm max-w-md mx-auto">
            Free plan shows your top 5 matches. Premium unlocks the rest, plus journey analytics.
          </p>
          <button onClick={() => setOpenPremium(true)} data-testid="matches-upgrade-btn"
            className="mt-5 inline-flex items-center gap-2 bg-coral text-white px-5 py-2.5 rounded-full font-medium hover:bg-coral-600 transition-colors">
            <Sparkle size={14} weight="duotone" /> Unlock all matches, $14.99/mo
          </button>
        </div>
      )}

      <PremiumDialog open={openPremium} onClose={() => setOpenPremium(false)} onUpgraded={load} />
    </div>
  );
}

function HiddenMatchesSection({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (!open) {
      setLoading(true);
      try {
        const { data } = await api.get("/matches", { params: { include_hidden: true, limit: 30 } });
        setItems(data.hidden_matches || []);
      } catch { toast.error("Could not load hidden"); }
      setLoading(false);
    }
    setOpen(!open);
  };

  return (
    <div className="mt-10 bg-white border border-border rounded-2xl p-5 shadow-soft" data-testid="hidden-matches-section">
      <button onClick={toggle} data-testid="hidden-toggle"
              className="w-full flex items-center justify-between gap-3 group">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-muted grid place-items-center shrink-0">
            <EyeSlash size={14} weight="duotone" className="text-muted-foreground" />
          </div>
          <div className="text-left">
            <div className="font-display text-base leading-tight">Hidden matches</div>
            <div className="text-xs text-muted-foreground">Quietly out of view. They were never notified.</div>
          </div>
        </div>
        {open ? <CaretUp size={16} className="text-muted-foreground" /> : <CaretDown size={16} className="text-muted-foreground" />}
      </button>
      {open && (
        <div className="mt-5 grid md:grid-cols-2 gap-4">
          {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
            : items.length === 0 ? <div className="text-sm text-muted-foreground">Nothing hidden.</div>
            : items.map(m => <MatchCard key={m.user_id} match={m} hidden onChanged={() => { onChanged?.(); toggle(); toggle(); }} />)}
        </div>
      )}
    </div>
  );
}
